import { createContext, useContext, useEffect, useState } from "react"

import { useReducedMotion } from "motion/react"

import { PRISM_GUIDING_MS } from "@/features/chat/components/prism-retrieval/prism-retrieval-constants"

/**
 * What a live Prism turn has shown so far (#3261): counters and timestamps
 * only, never a title. It outlives the component on purpose. The answer's
 * documents reach the chat with `message_saved`, and the same update gives
 * the answer its `messageId`, which is the chat's React key: the whole message
 * remounts at the moment Prism can first name a source. This record lets the
 * new instance continue the live sequence instead of starting over or jumping
 * to its final state.
 */
export type PrismTurnMemory = {
  /** The turn has settled into its final state; any later mount is static. */
  settled: boolean
  values: Map<string, number>
}

/** Turns remembered at once; the oldest is dropped first. */
const MAX_TURNS = 32
/**
 * How soon after a live Prism unmounts a new mount of the same turn carries
 * it on. The `message_saved` remount unmounts and mounts in one commit; a
 * user who leaves the chat and comes back later sees the final state.
 */
const PRISM_REMOUNT_MS = 1000
const turns = new Map<string, PrismTurnMemory>()

/** The remembered turn under `key`, if any. */
export function getPrismTurnMemory(key: string | undefined): PrismTurnMemory | undefined {
  return key === undefined ? undefined : turns.get(key)
}

function rememberPrismTurn(key: string, memory: PrismTurnMemory): PrismTurnMemory {
  // Deleted first, so the turn counts as the newest one and is dropped last.
  turns.delete(key)
  turns.set(key, memory)
  while (turns.size > MAX_TURNS) {
    const oldest = turns.keys().next().value
    if (oldest === undefined) break
    turns.delete(oldest)
  }
  return memory
}

function ensurePrismTurnMemory(key: string): PrismTurnMemory {
  return turns.get(key) ?? rememberPrismTurn(key, { settled: false, values: new Map() })
}

/** Test-only: forget every turn. */
export function resetPrismTurnMemory(): void {
  turns.clear()
}

/**
 * The turn key of the Prism around a view (stable across the `message_saved`
 * remount: the chat session and the message's position). Without one, views
 * keep no memory and behave as if every mount were their first.
 */
export const PrismTurnKeyContext = createContext<string | undefined>(undefined)

export function usePrismTurnKey(): string | undefined {
  return useContext(PrismTurnKeyContext)
}

/**
 * How many of the turn's cited sources an earlier mount of the same live turn
 * had already named (#3261). Once the answer's FILTH citations are known while
 * it streams, sources are named before the `message_saved` remount; the views
 * draw those at once, and play an entrance only for the ones named after it.
 */
export const PrismNamedBeforeContext = createContext(0)

export function usePrismNamedBefore(): number {
  return useContext(PrismNamedBeforeContext)
}

export type PrismTurn = {
  /**
   * The views run their live sequence. True while the answer is written, while
   * the cited library documents have not arrived yet, and while the cited
   * FILTH sources are still being named; then one Guiding beat so the last
   * name lands before the panel settles. With reduced motion: while the
   * answer is written or its documents are awaited.
   */
  live: boolean
  /** How many of the cited FILTH sources the views may name so far. */
  named: number
  /** Sources an earlier mount of this live turn already named; they do not enter again. */
  namedBefore: number
  /** The turn animates: it mounted live and motion is allowed. */
  motion: boolean
}

type PrismTurnOptions = {
  turnKey: string | undefined
  /** The answer is still being written (`!answer.completed`). */
  answering: boolean
  /** The answer is done but the documents that identify its citations are not. */
  awaitingSources: boolean
  /** Cited FILTH sources identified so far. */
  sourceCount: number
}

/**
 * Paces how Prism names the answer's cited FILTH sources (#3261). A source is
 * named no earlier than it is identified, and names follow one Guiding beat
 * apart in citation order, so each lands in its slot on its own. A turn that
 * is not live (history, a settled turn, reduced motion) names every source at
 * once and never animates.
 *
 * A turn mounted while its answer is written stays live, across remounts
 * under the same `turnKey`, until the answer is done, its documents have
 * arrived and every identified source is named.
 */
export function usePrismTurn({
  turnKey,
  answering,
  awaitingSources,
  sourceCount,
}: PrismTurnOptions): PrismTurn {
  const reduceMotion = useReducedMotion() === true
  const [initial] = useState(() => {
    const memory = getPrismTurnMemory(turnKey)
    // No `leftAt`: the live instance is still mounted, which is how a remount
    // renders its replacement (the old one unmounts in the same commit).
    const leftAt = memory?.values.get("leftAt")
    const carried =
      memory !== undefined &&
      !memory.settled &&
      (leftAt === undefined || Date.now() - leftAt <= PRISM_REMOUNT_MS)
    // A fresh live mount is a new turn, even at a position an earlier turn
    // held (a retry, or an empty answer sent again): it starts from a clean
    // record, so its remount carries it on and no view resumes the old one.
    // Here rather than in an effect: the views read the record while they
    // render, right after this.
    if (answering && !carried && turnKey !== undefined) {
      rememberPrismTurn(turnKey, { settled: false, values: new Map() })
    }
    return {
      mountedLive: answering || carried,
      named: carried ? (memory.values.get("named") ?? 0) : 0,
    }
  })
  const motion = initial.mountedLive && !reduceMotion
  const [named, setNamed] = useState(initial.named)
  const [settled, setSettled] = useState(false)
  const shown = motion ? Math.min(named, sourceCount) : sourceCount
  const pending = answering || awaitingSources || shown < sourceCount
  // Without motion nothing is paced, but the turn still holds its live
  // structure until the documents have told its sources apart, so the panel
  // changes once, when the saved message arrives, not twice.
  const live = motion ? !settled : answering || awaitingSources

  // Remember the turn while it is live, so a remount can carry it on.
  useEffect(() => {
    if (!motion || turnKey === undefined) return
    const memory = ensurePrismTurnMemory(turnKey)
    if (settled) memory.settled = true
    else memory.values.set("named", named)
  }, [motion, turnKey, named, settled])

  // Note when this instance leaves: only a mount right after it continues.
  useEffect(() => {
    if (!motion || turnKey === undefined) return
    ensurePrismTurnMemory(turnKey).values.delete("leftAt")
    return () => {
      getPrismTurnMemory(turnKey)?.values.set("leftAt", Date.now())
    }
  }, [motion, turnKey])

  // Name the next identified source, one Guiding beat after the one before.
  useEffect(() => {
    if (!motion || settled || named >= sourceCount) return
    const memory = getPrismTurnMemory(turnKey)
    const last = memory?.values.get("namedAt") ?? Number.NEGATIVE_INFINITY
    const delay = Math.max(0, last + PRISM_GUIDING_MS - Date.now())
    const timer = setTimeout(() => {
      memory?.values.set("namedAt", Date.now())
      setNamed(named + 1)
    }, delay)
    return () => clearTimeout(timer)
  }, [motion, settled, named, sourceCount, turnKey])

  // Settle one Guiding beat after nothing is pending any more.
  useEffect(() => {
    if (!motion || settled || pending) return
    const timer = setTimeout(() => setSettled(true), PRISM_GUIDING_MS)
    return () => clearTimeout(timer)
  }, [motion, settled, pending])

  return { live, named: shown, namedBefore: motion ? initial.named : 0, motion }
}
